import { useAuth } from "@/src/contexts/auth.context";
import {
  ApiNewsCategoryWithCount,
  BlocksApiNews,
  DeleteNewsOptions,
} from "@/src/models/news.model";
import {
  NEWS_ALL_QUERY_KEY,
  NEWS_BY_CATEGORY_QUERY_KEY,
  NEWS_CATEGORIES_QUERY_KEY,
  NEWS_DRAFT_QUERY_KEY,
  NEWS_ITEM_QUERY_KEY,
  NEWS_SAVED_QUERY_KEY,
} from "@/src/query-keys/news.keys";
import { useGetCategoriesQuery } from "@/src/services/api/category/category.hooks";
import gqlClient from "@/src/services/api/graphql.client";
import {
  DELETE_NEWS,
  GET_NEWS,
  GET_NEWS_ITEM,
  UPDATE_NEWS,
} from "@/src/services/api/news/news.query";
import { useGetUserProfileQuery } from "@/src/services/profile/profile.hooks";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

export const DEFAULT_AUTHOR_AVATAR = require("@/src/assets/images/profile.png");

const ALL_NEWS_PAGE_SIZE = 6;

const request = async (query: string, variables?: object) => {
  const client = await gqlClient();
  return client.request(query, variables);
};

const fetchBlocksNews = async (
  filter: object,
  pageSize: number,
  pageNo = 1,
): Promise<{ items: BlocksApiNews[]; totalCount: number }> => {
  const data: any = await request(GET_NEWS, {
    filter: JSON.stringify(filter),
    sort: JSON.stringify({ CreatedDate: -1 }),
    pageSize,
    pageNo,
  });

  return {
    items: data?.getnewss?.items ?? [],
    totalCount: data?.getnewss?.totalCount ?? 0,
  };
};

const buildFilter = (
  extra: object,
  search?: string,
  options?: { isDraft?: boolean; isPinned?: boolean; visibility?: string },
): object => {
  const filter: Record<string, unknown> = { ...extra };
  if (options?.isDraft !== undefined) {
    filter.isDraft = options.isDraft;
  }
  if (options?.isPinned !== undefined) {
    filter.isPinned = options.isPinned;
  }
  if (options?.visibility !== undefined) {
    filter.visibility = options.visibility;
  }
  if (search?.trim()) {
    filter.Title = { $regex: search.trim(), $options: "i" };
  }
  return filter;
};

export const useNewsItem = (itemId?: string, enabled = true) =>
  useQuery({
    queryKey: [...NEWS_ITEM_QUERY_KEY, itemId ?? ""],
    queryFn: async () => {
      const client = await gqlClient();
      const data: any = await client.request(GET_NEWS_ITEM, {
        filter: JSON.stringify({
          ItemId: itemId,
        }),
      });
      return data?.getnewss?.items?.[0] ?? null;
    },
    enabled: enabled && !!itemId,
  });

export const useAllNews = (search?: string, groupId?: string) =>
  useInfiniteQuery({
    queryKey: [...NEWS_ALL_QUERY_KEY, groupId ?? "public", search ?? ""],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      fetchBlocksNews(
        groupId
          ? buildFilter(
            { groupIds: groupId },
            search,
            { isDraft: false, visibility: "Group" },
          )
          : buildFilter({}, search, { isDraft: false, visibility: "Public" }),
        ALL_NEWS_PAGE_SIZE,
        pageParam as number,
      ),
    getNextPageParam: (lastPage, allPages) => {
      const fetched = allPages.reduce((t, p) => t + p.items.length, 0);
      return fetched < lastPage.totalCount ? allPages.length + 1 : undefined;
    },
  });

export const usePinnedNews = (groupId?: string) =>
  useQuery({
    queryKey: [...NEWS_ALL_QUERY_KEY, "pinned", groupId ?? "public"],
    queryFn: () =>
      fetchBlocksNews(
        groupId
          ? buildFilter(
            { groupIds: groupId },
            undefined,
            { isDraft: false, isPinned: true, visibility: "Group" },
          )
          : buildFilter({}, undefined, {
            isDraft: false,
            isPinned: true,
            visibility: "Public",
          }),
        100,
      ).then((result) => result.items),
    staleTime: 5 * 60 * 1000,
  });

export const useTogglePinNews = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      itemId,
      isPinned,
    }: {
      itemId: string;
      isPinned: boolean;
    }) => {
      await request(UPDATE_NEWS, {
        filter: JSON.stringify({ ItemId: itemId }),
        input: { isPinned },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NEWS_ALL_QUERY_KEY });
    },
  });
};

export const useNewsByCategory = (categoryId: string, search?: string) =>
  useQuery({
    queryKey: [...NEWS_BY_CATEGORY_QUERY_KEY, categoryId, search ?? ""],
    queryFn: () =>
      fetchBlocksNews(
        buildFilter({ categoryIds: { $in: [categoryId] } }, search, {
          isDraft: false,
        }),
        100,
      ).then((result) => result.items),
    enabled: !!categoryId,
    staleTime: 5 * 60 * 1000,
  });

export const useNewsCategoriesQuery = () => {
  const { data } = useGetCategoriesQuery({
    tag: "news",
    pageSize: 100,
  });

  const rawCategories = data?.items ?? [];

  return useQuery({
    queryKey: [
      ...NEWS_CATEGORIES_QUERY_KEY,
      rawCategories.map((c) => c.ItemId).join(","),
    ],
    queryFn: async (): Promise<ApiNewsCategoryWithCount[]> => {
      const { items: allNews } = await fetchBlocksNews(
        buildFilter({}, undefined, { isDraft: false }),
        1000,
      );

      const categoryMap = new Map<string, string>(
        rawCategories.map((c) => [String(c.ItemId), String(c.categoryName)]),
      );

      const articleCountByCategoryId = new Map<
        string,
        ApiNewsCategoryWithCount
      >();

      for (const newsItem of allNews) {
        for (const categoryId of newsItem.categoryIds ?? []) {
          const key = String(categoryId);
          const name = categoryMap.get(key) ?? key;

          if (articleCountByCategoryId.has(key)) {
            articleCountByCategoryId.get(key)!.articleCount += 1;
          } else {
            articleCountByCategoryId.set(key, {
              categoryID: key,
              name,
              articleCount: 1,
            });
          }
        }
      }

      return Array.from(articleCountByCategoryId.values());
    },
    enabled: rawCategories.length > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useNewsSaveState = (search?: string) => {
  const { user } = useAuth();
  const userId = user?.itemId ?? "";
  const { data: profile } = useGetUserProfileQuery(userId);

  const savedArticleIds: string[] = Array.isArray(profile?.newsSaved)
    ? profile.newsSaved
    : [];

  const { data: savedNews = [], isLoading } = useQuery({
    queryKey: [...NEWS_SAVED_QUERY_KEY, userId, search ?? ""],
    queryFn: async () => {
      if (!savedArticleIds.length) return [];

      const result = await fetchBlocksNews(
        { ItemId: { $in: savedArticleIds } },
        savedArticleIds.length,
      );

      return result.items;
    },
    enabled: !!userId && !!profile,
  });

  const filtered = search?.trim()
    ? savedNews.filter(
      (item) =>
        item?.Title?.toLowerCase().includes(search.toLowerCase()) ||
        item?.Content?.toLowerCase().includes(search.toLowerCase()),
    )
    : savedNews;

  return {
    savedNews: filtered,
    isSaved: (id: string) => savedArticleIds.includes(id),
    isLoadingSavedNews: isLoading,
    search,
  };
};

export const useDraftNews = (search?: string) => {
  const { user } = useAuth();
  const userId = user?.itemId ?? "";
  const { data: profile } = useGetUserProfileQuery(userId);

  const draftIds = Array.isArray(profile?.newsDraftIds)
    ? profile.newsDraftIds
    : [];

  return useQuery({
    queryKey: [...NEWS_DRAFT_QUERY_KEY, userId, draftIds, search ?? ""],
    queryFn: async () => {
      if (!draftIds.length) return [];

      const result = await fetchBlocksNews(
        buildFilter({ ItemId: { $in: draftIds } }, search, { isDraft: true }),
        draftIds.length,
      );

      return result.items;
    },
    enabled: !!userId && draftIds.length > 0,
  });
};

export const useDeleteNews = ({
  categoryId,
  onSuccess,
}: DeleteNewsOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (itemId: string) => {
      await request(DELETE_NEWS, {
        filter: JSON.stringify({ ItemId: itemId }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NEWS_ALL_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: NEWS_BY_CATEGORY_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: NEWS_CATEGORIES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: NEWS_SAVED_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: NEWS_DRAFT_QUERY_KEY });

      if (categoryId) {
        queryClient.invalidateQueries({
          queryKey: [...NEWS_BY_CATEGORY_QUERY_KEY, categoryId],
        });
      }

      onSuccess?.();
    },
  });
};
